import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { Home, FileText, Newspaper, Mic, BookOpen, Inbox, LogOut, Menu, X, ExternalLink, KeyRound, Settings2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: Home, end: true },
  { to: "/admin/articles", label: "Articles", icon: FileText },
  { to: "/admin/news", label: "News", icon: Newspaper },
  { to: "/admin/interviews", label: "Interviews", icon: Mic },
  { to: "/admin/magazines", label: "Magazines", icon: BookOpen },
  { to: "/admin/site-settings", label: "Site Settings", icon: Settings2 },
];

const MESSAGE_NAV = [
  { to: "/admin/messages/contact", label: "Contact" },
  { to: "/admin/messages/newsletter", label: "Newsletter" },
  { to: "/admin/messages/featured", label: "Featured Applications" },
];

const AdminLayout = ({ user }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const logout = async () => {
    try { await api.logout(); } catch {}
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-cream text-ink flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-72 bg-paper border-r border-ink-line z-40 transform transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } flex flex-col`}
        data-testid="admin-sidebar"
      >
        <div className="px-6 py-6 border-b border-ink-line">
          <Link to="/admin" className="block" data-testid="admin-sidebar-logo"><Logo size="sm" /></Link>
          <div className="overline overline-muted mt-4">Editorial Console</div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          <div className="px-4 mb-3 overline overline-muted text-[10px]">Content</div>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "text-ink border-ink bg-cream/60 font-medium"
                    : "text-ink-soft border-transparent hover:text-ink hover:bg-cream/40"
                }`
              }
            >
              <n.icon size={16} strokeWidth={1.4} />
              {n.label}
            </NavLink>
          ))}

          <div className="mt-8 px-4 mb-3 overline overline-muted text-[10px]">Inbox</div>
          {MESSAGE_NAV.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              onClick={() => setOpen(false)}
              data-testid={`admin-nav-msg-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "text-ink border-ink bg-cream/60 font-medium"
                    : "text-ink-soft border-transparent hover:text-ink hover:bg-cream/40"
                }`
              }
            >
              <Inbox size={16} strokeWidth={1.4} />
              {m.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-ink-line px-6 py-4">
          {user && (
            <div className="flex items-center gap-3 mb-3">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-ink-line" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-cream border border-ink-line" />
              )}
              <div className="min-w-0">
                <div className="text-sm text-ink truncate">{user.name || user.email}</div>
                <div className="text-[11px] text-ink-muted truncate">{user.email}</div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mb-2">
            <NavLink
              to="/admin/change-password"
              data-testid="admin-change-password-link"
              className={({ isActive }) =>
                `flex-1 inline-flex items-center justify-center gap-1 text-xs border rounded-full px-3 py-2 transition-colors ${
                  isActive ? "bg-ink text-paper border-ink" : "text-ink-soft hover:text-ink border-ink-line"
                }`
              }
            >
              <KeyRound size={12} /> Change password
            </NavLink>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-view-site"
              className="flex-1 inline-flex items-center justify-center gap-1 text-xs text-ink-soft hover:text-ink border border-ink-line rounded-full px-3 py-2"
            >
              <ExternalLink size={12} /> View site
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1 text-xs text-ink-soft hover:text-ink border border-ink-line rounded-full px-3 py-2"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && <div className="fixed inset-0 bg-ink/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-paper/95 backdrop-blur border-b border-ink-line">
          <div className="px-4 md:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setOpen((v) => !v)}
                data-testid="admin-mobile-toggle"
                className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full border border-ink-line"
                aria-label={open ? "Close menu" : "Open menu"}
              >
                {open ? <X size={16} /> : <Menu size={16} />}
              </button>
              <div className="font-display text-xl md:text-2xl text-ink">Editorial Console</div>
            </div>
          </div>
        </header>

        <main className="p-4 md:p-8 min-h-[calc(100vh-4rem)]" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
